import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, normalizeStyle as _normalizeStyle, vShow as _vShow, withDirectives as _withDirectives, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = ["inert"]
const _hoisted_2 = ["inert"]
const _hoisted_3 = {
  key: 0,
  style: {"width":"100%","height":"100%"}
}
const _hoisted_4 = { class: "dialogue-workspace" }
const _hoisted_5 = { class: "dialogue-export-toolbar" }
const _hoisted_6 = {
  class: "dialogue-view-switch",
  role: "group",
  "aria-label": "预览风格"
}
const _hoisted_7 = ["aria-pressed"]
const _hoisted_8 = ["aria-pressed"]
const _hoisted_9 = {
  key: 0,
  class: "dialogue-history-tools",
  "data-dialogue-history-tools": "",
  role: "group",
  "aria-label": "编辑历史"
}
const _hoisted_10 = ["disabled"]
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { key: 1 }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = { class: "dialogue-editing-body" }
const _hoisted_15 = { class: "dialogue-editing-stage" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = {
  key: 0,
  role: "alert"
}
const _hoisted_18 = {
  key: 1,
  role: "alert"
}
const _hoisted_19 = ["inert"]
const _hoisted_20 = {
  key: 1,
  class: "studio-empty"
}

import SectionLayout from "@/components/Layout/SectionLayout.vue";
import StudioFileList from "../StudioFileList.vue";
import StudioCreateDialog from "../StudioCreateDialog.vue";
import type { StudioEditorKind } from "../studioSidebar";
import StudioSidebarContent from "../StudioSidebarContent.vue";
import { StorageClass } from "@/services/storage/storage";
import { downloadJsonFile } from "@/utils/download";
import { consola } from "consola";
import {
  computed,
  inject,
  provide,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  watch,
  watchEffect,
} from "vue";
import {
  DialogueEditorID,
  DialogueEntry,
  ProjectID,
} from "../../constant/constant";
import { toast } from "vue-sonner";
import { useWorkspaceStructIds } from "../useWorkspaceStructIds";
import {
  VueFlow,
  useVueFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeChange,
} from "@vue-flow/core";
import DropzoneBackground from "./DropzoneBackground.vue";
import Sidebar from "./Sidebar.vue";
import useDragAndDrop, { DIALOGUE_NODE_GRID_SIZE } from "./useDnD";
import GroupNode from "./GroupNode.vue";
import ConditionBranchNode from "./ConditionBranchNode.vue";
import GroupTimeline from "./GroupTimelineV3.vue";
import TimelinePanel from "./components/TimelinePanel.vue";
import DialogueTextPreview from "./DialogueTextPreview.vue";
import { layoutDialogueGraph, type GraphNodeSize } from "./utils/dialogueGraphLayout";
import { applyDialogueTextEdit, applyDialogueOptionIconEdit, type DialogueTextEdit } from "./utils/dialogueTextEditing";
import { useEntityPresets } from "../EntityPresetEditor/useEntityPresets";
import { usePublicEventPresets } from "../EntityPresetEditor/usePublicEventPresets";
import { syncDialoguePublicEventVisibility } from "./utils/publicEventParameters";
import { syncStyleParameters } from "./utils/styleParameters";
import { useStylePresets } from "../EntityPresetEditor/stylePresets";
import { createWorkspaceSaveQueue } from "../QuestEditor/workspaceSaveQueue";
import { bindWorkspaceSaveLifecycle } from "../QuestEditor/workspaceSaveLifecycle";
import { resolveTextPreviewGraphNodeId, type TextPreviewNavigationTarget } from "./utils/dialogueTextNavigation";
import type {
  DialogueProject,
  FlowNodeData,
} from "./types/FileStruct";
import Entry from "./components/Entry.vue";
import { useDialogueHistory } from "./useDialogueHistory";
import { studioEditorActiveKey, studioSessionHistoryKey } from "../studioSessionHistory";
import { useDialogueGraphClipboard } from "./useDialogueGraphClipboard";
import { pasteDialogueGraphNodes, type DialogueGraphClipboard } from "./utils/dialogueGraphClipboard";
import {
  createConditionBranchNode,
  createDialogueNode,
  createEmptyDialogueProject,
} from "./utils/dialogueProject";
import {
  decodeDialogueProject,
  encodeDialogueProject,
} from "./utils/dialogueProjectCodec";
import { exportQxqyPerformance, resolveGroupOrder } from "./utils/qxqyPerformanceExporter";
import RuntimeImportButton from "../RuntimeImportButton.vue";
import { commitRuntimeImport } from "../runtimeImportStorage";
import { importQxqyPerformance } from "./utils/qxqyPerformanceImporter";
import {
  normalizeSourceHandle,
  resolveGroupOutlets,
} from "./utils/groupOutlets";


export default /*@__PURE__*/_defineComponent({
  __name: 'DialogueEditor',
  props: {
    editorKind: { default: "Dialogue" }
  },
  emits: ["update:editorKind"],
  setup(__props: any, { expose: __expose, emit: __emit }) {



const emit = __emit;
const editorActive = inject(studioEditorActiveKey, () => true);
const studioSession = inject(studioSessionHistoryKey, undefined);
const { availablePresets: entityPresets, error: entityPresetsError, retry: retryEntityPresets } = useEntityPresets();
const dialogueStylePresets = useStylePresets("dialogueStyles");
const selectStylePresets = useStylePresets("selectStyles");
const publicEventPresets = usePublicEventPresets();
provide("dialogueStyleOptions", dialogueStylePresets.options);
provide("selectStyleOptions", selectStylePresets.options);
provide("dialogueStylePresetsReady", dialogueStylePresets.ready);
provide("selectStylePresetsReady", selectStylePresets.ready);

const {
  onConnect, onNodesChange, getSelectedNodes, getSelectedEdges, nodesSelectionActive,
  findNode, fitView, addSelectedNodes, removeSelectedNodes, removeSelectedEdges,
} = useVueFlow();

const dialogueProject = ref<DialogueProject>(); //读取文件后的对话内容
watchEffect(() => {
  if (dialogueProject.value && publicEventPresets.ready.value) {
    syncDialoguePublicEventVisibility(dialogueProject.value, publicEventPresets.availablePresets.value);
  }
}, { flush: "sync" });
function syncProjectStyleParameters() {
  const project = dialogueProject.value;
  if (!project) return;
  for (const node of Object.values(project.dialogue.nodes)) {
    if (dialogueStylePresets.ready.value) {
      for (const clip of [node.dialogue, ...(node.additionalDialogues ?? [])]) {
        if (clip) syncStyleParameters(clip, dialogueStylePresets.options.value);
      }
    }
    if (node.select && selectStylePresets.ready.value) {
      syncStyleParameters(node.select, selectStylePresets.options.value);
    }
  }
}
watchEffect(syncProjectStyleParameters);
const exportedGroupIndices = computed(() => new Map(
  dialogueProject.value ? resolveGroupOrder(dialogueProject.value).map((id, index) => [id, index] as const) : [],
));
const selectedGroupNodeId = ref("");
const timelinePanelHeight = ref(368);
const clipInspectorTarget = ref<HTMLElement>();
const clipInspectorOpen = ref(false);
const editorView = ref<"graph" | "text">("text");
const arrangingGraph = ref(false);
const workspaceIds = useWorkspaceStructIds();
const selectedGroupNode = computed(() =>
  selectedGroupNodeId.value && dialogueProject.value
    ? dialogueProject.value.dialogue.nodes[selectedGroupNodeId.value]
    : undefined,
);

function AddDialogueNode(nodeId: string) {
  if (!dialogueProject.value) return;
  dialogueProject.value.dialogue.nodes[nodeId] = createDialogueNode(nodeId);
}

function AddConditionBranchNode(nodeId: string) {
  if (!dialogueProject.value) return;
  dialogueProject.value.dialogue.conditionBranches[nodeId] =
    createConditionBranchNode(nodeId);
}

function UpdateGraphNodeAnnotation(graphNodeId: string, annotation: string) {
  const graphNode = dialogueProject.value?.graph.nodes.find((node) => node.id === graphNodeId);
  if (!graphNode) return;
  graphNode.data = { ...graphNode.data, annotation };
}

const { onDragOver, onDrop, onDragLeave, isDragOver } =
  useDragAndDrop(AddDialogueNode, AddConditionBranchNode);

function IsValidConnection(connection: Connection) {
  return connection.source !== connection.target;
}

function AddGraphEdge(connection: Connection) {
  const project = dialogueProject.value;
  if (!project || !IsValidConnection(connection)) return;

  const sourceHandle = normalizeSourceHandle(connection.sourceHandle);
  const isSameOutput = (edge: Edge) => {
    const edgeSourceHandle = normalizeSourceHandle(edge.sourceHandle);
    return (
      edge.source === connection.source &&
      edgeSourceHandle === sourceHandle
    );
  };
  const nextEdge: Edge = {
    id: `edge_${crypto.randomUUID()}`,
    source: connection.source,
    target: connection.target,
    sourceHandle,
    targetHandle: connection.targetHandle,
  };

  // 删除旧线和添加新线必须在同一次响应式更新中完成，否则 v-model
  // 会先渲染“已断开”的中间状态，导致第一次重连丢失。
  project.graph.edges = [
    ...project.graph.edges.filter((edge) => !isSameOutput(edge)),
    nextEdge,
  ];
}

onConnect(AddGraphEdge);
onNodesChange((changes: NodeChange[]) => {
  if (!dialogueProject.value) return;

  for (const change of changes) {
    if (change.type === "remove") {
      delete dialogueProject.value.dialogue.nodes[change.id];
      delete dialogueProject.value.dialogue.conditionBranches[change.id];
      if (selectedGroupNodeId.value === change.id) {
        selectedGroupNodeId.value = "";
      }
    }
  }
});

function SelectGraphNode(event: { node: Node<FlowNodeData> }) {
  selectedGroupNodeId.value = event.node.data?.dialogueNodeId ?? "";
}

function ChangeEditorView(view: "graph" | "text", arrange = true) {
  const enteringGraph = view === "graph" && editorView.value !== "graph";
  editorView.value = view;
  selectedGroupNodeId.value = "";
  if (enteringGraph && arrange) void ArrangeGraph();
}

function ApplyGraphLayout(project: DialogueProject) {
  const sizes = new Map<string, GraphNodeSize>();
  const outletOrder = new Map<string, string[]>();
  for (const node of project.graph.nodes) {
    const dimensions = findNode(node.id)?.dimensions;
    if (dimensions) sizes.set(node.id, dimensions);
    const group = project.dialogue.nodes[node.data?.dialogueNodeId ?? node.id];
    const branch = project.dialogue.conditionBranches[node.data?.conditionBranchNodeId ?? node.id];
    if (group) outletOrder.set(node.id, resolveGroupOutlets(group).outlets.map(outlet => outlet.id));
    else if (branch) outletOrder.set(node.id, branch.outputs.map(output => output.id));
  }
  project.graph.nodes = layoutDialogueGraph(project.graph, sizes, outletOrder);
}

async function ArrangeGraph() {
  const project = dialogueProject.value;
  if (!project || arrangingGraph.value) return;
  arrangingGraph.value = true;
  try {
    await nextTick();
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    if (dialogueProject.value !== project || editorView.value !== "graph") return;
    ApplyGraphLayout(project);
    await nextTick();
    if (dialogueProject.value !== project || editorView.value !== "graph") return;
    nodesSelectionActive.value = false;
    await fitView({ padding: 0.2, minZoom: 0.05, maxZoom: 1, duration: 250 });
  } catch (error) {
    consola.error(error);
    toast.error("节点排列失败，请重试");
  } finally {
    arrangingGraph.value = false;
  }
}

function EditDialogueText(edit: DialogueTextEdit) {
  if (dialogueProject.value) applyDialogueTextEdit(dialogueProject.value, edit);
}
function EditDialogueOption(nodeId: string, optionId: string, value: string) {
  const option = dialogueProject.value?.dialogue.nodes[nodeId]?.select?.options.find((item) => item.id === optionId);
  if (option) option.content = value;
}
function EditDialogueOptionIcon(nodeId: string, optionId: string, icon: number) {
  if (dialogueProject.value) applyDialogueOptionIconEdit(dialogueProject.value, nodeId, optionId, icon);
}

async function NavigateToPreviewNode(target: TextPreviewNavigationTarget) {
  const project = dialogueProject.value;
  if (!project) return;
  const graphNodeId = resolveTextPreviewGraphNodeId(project, target);
  if (!graphNodeId) {
    toast.warning("该内容没有对应的可见画布节点，无法定位");
    return;
  }

  ChangeEditorView("graph", false);
  nodesSelectionActive.value = false;
  // 先展开目标 Timeline，再测量画布，避免定位后被下方 Timeline 挤出可视区域。
  selectedGroupNodeId.value = target.kind === "dialogue" || target.kind === "select" || target.kind === "action"
    ? target.nodeId : "";
  await nextTick();
  // v-show 恢复后，需要让 ResizeObserver 更新 Vue Flow 的视口/节点尺寸。
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  if (dialogueProject.value !== project || editorView.value !== "graph") return;
  const node = findNode(graphNodeId);
  if (!node || node.hidden) return;
  ApplyGraphLayout(project);
  await nextTick();
  if (dialogueProject.value !== project || editorView.value !== "graph") return;
  removeSelectedNodes(getSelectedNodes.value);
  removeSelectedEdges(getSelectedEdges.value);
  addSelectedNodes([node]);
  await fitView({ nodes: [graphNodeId], padding: 0.35, maxZoom: 1, duration: 250 });
}

async function FinishGraphSelection() {
  const inspectorWasOpen = clipInspectorOpen.value;
  // 等待框选产生的 selected 状态同步后，再决定是否打开 Timeline。
  await nextTick();
  const selectedNodes = getSelectedNodes.value;
  if (selectedNodes.length === 1) {
    // 单选不保留群组选框遮罩，避免挡住节点的引脚和参数输入框。
    nodesSelectionActive.value = false;
    SelectGraphNode({ node: selectedNodes[0] });
  } else if (selectedNodes.length > 1 || !inspectorWasOpen) {
    selectedGroupNodeId.value = "";
  }
}

const storage = inject<StorageClass>("storage")!.setProject(ProjectID); //储存区
const workspaceId = inject<Ref<string>>("selectedWorkspaceId")!;
// 此组件按工作区重建；保存路径固定在创建时，不能跟随之后的工作区选择变化。
const documentWorkspaceId = workspaceId.value;
let disposed = false;
let loadingFile = false;
let fileRequest = 0;
let listRequest = 0;
const fileBusy = ref(false);
const dialogueEditorElement = ref<HTMLElement>();
const dialogueHistory = useDialogueHistory({
  project: dialogueProject,
  element: dialogueEditorElement,
  blocked: () => fileBusy.value || arrangingGraph.value,
  afterRestore: () => { selectedGroupNodeId.value = ""; clipInspectorOpen.value = false; },
  onError: error => toast.error(error instanceof Error ? error.message : "对话历史恢复失败"),
});

useDialogueGraphClipboard({
  project: dialogueProject,
  element: dialogueEditorElement,
  enabled: () => editorActive() && editorView.value === "graph" && !fileBusy.value && !arrangingGraph.value && !dialogueHistory.busy.value,
  selectedNodeIds: () => getSelectedNodes.value.map(node => node.id),
  paste: PasteGraphNodes,
  onCopy: count => toast.success(`已复制 ${count} 个节点`),
  onError: error => toast.error(error instanceof Error ? error.message : "节点复制或粘贴失败"),
});

function PasteGraphNodes(clipboard: DialogueGraphClipboard, offset: number) {
  if (!dialogueProject.value) return false;
  const result = pasteDialogueGraphNodes(dialogueProject.value, clipboard, offset);
  dialogueHistory.finish();
  dialogueHistory.begin("paste");
  try {
    removeSelectedNodes(getSelectedNodes.value);
    removeSelectedEdges(getSelectedEdges.value);
    nodesSelectionActive.value = false;
    clipInspectorOpen.value = false;
    dialogueProject.value = result.project;
    selectedGroupNodeId.value = result.nodeIds.length === 1 && result.project.dialogue.nodes[result.nodeIds[0]] ? result.nodeIds[0] : "";
  } finally { dialogueHistory.end("paste"); }
  void FocusPastedNodes(dialogueProject.value, result.nodeIds).catch(error => { consola.error(error); });
  return true;
}

async function FocusPastedNodes(project: DialogueProject, nodeIds: string[]) {
  await nextTick();
  await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  if (disposed || dialogueProject.value !== project || editorView.value !== "graph") return;
  const nodes = nodeIds.map(id => findNode(id)).filter((node): node is NonNullable<typeof node> => !!node);
  addSelectedNodes(nodes);
  await fitView({ nodes: nodeIds, padding: 0.3, maxZoom: 1, duration: 200 });
}

function AssemblyPath(path: string) {
  return `/${documentWorkspaceId}/${DialogueEditorID}${path}`;
}

const dialogueFiles = ref<string[]>([]);
const selectedDialogueFile = ref<string>("");
const newDialogueFileOpen = ref(false);
const newDialogueFileName = ref("");

watch(
  dialogueProject,
  () => {
    SynchronizeGraphEdges();
    scheduleSave();
  },
  { deep: true, flush: "sync" },
);

/** Timeline 改变出口数量时，清理已经不存在的 Handle 对应连线。 */
function SynchronizeGraphEdges() {
  const project = dialogueProject.value;
  if (!project) return;

  const nextEdges = project.graph.edges.filter((edge) => {
    const sourceNode = project.dialogue.nodes[edge.source];
    if (!sourceNode) {
      const conditionBranch =
        project.dialogue.conditionBranches[edge.source];
      if (!conditionBranch) return true;
      return conditionBranch.outputs.some(
        (output) => output.id === edge.sourceHandle,
      );
    }

    const validHandles = new Set(
      resolveGroupOutlets(sourceNode).outlets.map((outlet) => outlet.id),
    );
    return validHandles.has(normalizeSourceHandle(edge.sourceHandle));
  });

  if (nextEdges.length !== project.graph.edges.length) {
    project.graph.edges = nextEdges;
  }
}

const saveQueue = createWorkspaceSaveQueue(
  (path, data) => storage.setProject(ProjectID).writeFile(path, data),
  (error) => { consola.error(error); toast.error("对话保存失败，请勿关闭页面并重试"); },
);
__expose({ prepareToLeave: async () => {
  if (fileBusy.value) throw new Error("对话文件正在读写，请稍后切换");
  dialogueHistory.finish();
  await nextTick();
  await saveQueue.flush();
  selectedGroupNodeId.value = "";
  clipInspectorOpen.value = false;
} });

function scheduleSave() {
  if (loadingFile || disposed || !selectedDialogueFile.value || !dialogueProject.value) return;
  saveQueue.schedule(AssemblyPath(`/${selectedDialogueFile.value}`), encodeDialogueProject(dialogueProject.value));
}

async function RefreshDialogueFile() {
  const request = ++listRequest;
  consola.debug("刷新对话文件");
  const files = await storage.setProject(ProjectID).getFiles(
    `/${documentWorkspaceId}/${DialogueEditorID}`,
  );
  if (!disposed && request === listRequest) dialogueFiles.value = files;
  consola.trace(dialogueFiles.value);
}

async function SelectDialogueFile(id: string) {
  if (fileBusy.value) return;
  fileBusy.value = true;

  selectedGroupNodeId.value = "";
  const request = ++fileRequest;
  try {
    await saveQueue.flush();
    const loaded = decodeDialogueProject(await storage.setProject(ProjectID).readFile(AssemblyPath(`/${id}`)));
    if (disposed || request !== fileRequest) return;
    loadingFile = true;
    selectedGroupNodeId.value = "";

    selectedDialogueFile.value = id;
    dialogueProject.value = loaded;
    // Vue Flow snaps loaded positions during rendering; this is the load baseline.
    await nextTick();
    if (disposed || request !== fileRequest) return;
    dialogueHistory.reset();
    loadingFile = false;
  } catch (error) { consola.error(error); toast.error("读取对话失败，原有内容已保留"); }
  finally { fileBusy.value = false; loadingFile = false; }
}
async function AddDialogueFile() {
  if (fileBusy.value) return;
  const input = newDialogueFileName.value;
  const base = input.trim().replace(/\.json$/i, "");
  if (!base || /[<>:"/\\|?*\u0000-\u001f]/.test(base) || base === "." || base === "..") {
    toast.warning("请输入有效的对话文件名"); return;
  }
  const fileName = `${base}.json`;
  fileBusy.value = true;

  selectedGroupNodeId.value = "";
  try {
    const filePath = AssemblyPath(`/${fileName}`);
    if (await storage.setProject(ProjectID).exists(filePath)) { toast.warning("已有同名对话文件"); return; }
    await saveQueue.flush();
    await storage.setProject(ProjectID).writeFile(filePath, encodeDialogueProject(createEmptyDialogueProject()));
    await RefreshDialogueFile();
    newDialogueFileOpen.value = false;
    newDialogueFileName.value = "";
  } catch (error) { consola.error(error); toast.error("创建对话文件失败"); }
  finally { fileBusy.value = false; }
  if (!newDialogueFileOpen.value) await SelectDialogueFile(fileName);
}
async function importConfiguration(file: File) {
  if (fileBusy.value || disposed) return;
  fileBusy.value = true;
  try {
    const result = await commitRuntimeImport({ file, decode: importQxqyPerformance, encode: result => encodeDialogueProject(result.project),
      storage: storage.setProject(ProjectID), active: () => !disposed, flush: () => saveQueue.flush(),
      directory: `/${documentWorkspaceId}/${DialogueEditorID}` });
    if (!result) return;
    loadingFile = true; selectedGroupNodeId.value = "";
    selectedDialogueFile.value = result.name; dialogueProject.value = result.project.project;
    await nextTick();
    if (disposed) return;
    dialogueHistory.reset();
    loadingFile = false;
    newDialogueFileOpen.value = false;
    await RefreshDialogueFile();
    if (!disposed) { toast.success(`已新增「${result.name}」`); toast.warning(result.project.warnings.join("；")); }
  } finally { loadingFile = false; if (!disposed) fileBusy.value = false; }
}
async function DeleteDialogueFile(isForce = false) {
  if (fileBusy.value) return;

  if (selectedDialogueFile.value == "") {
    toast.warning("未选择任何对话文件");
    return;
  }

  if (
    isForce ||
    confirm(`确认要删除对话文件【${selectedDialogueFile.value}】吗？`)
  ) {
    const fileName = selectedDialogueFile.value;
    fileBusy.value = true;

    selectedGroupNodeId.value = "";
    fileRequest++;
    try {
      await saveQueue.flush();
      await storage.setProject(ProjectID).trash(AssemblyPath(`/${fileName}`));
      saveQueue.discard(AssemblyPath(`/${fileName}`));
      selectedDialogueFile.value = "";
      selectedGroupNodeId.value = "";

      dialogueProject.value = undefined;
      dialogueHistory.reset();
      await RefreshDialogueFile();
      toast.success(`已删除 ${fileName}`);
    } catch (error) { consola.error(error); toast.error("对话删除失败，文件已保留"); }
    finally { fileBusy.value = false; }
  }
}

async function DownloadQxqyPerformanceFile() {
  if (!selectedDialogueFile.value || !dialogueProject.value) {
    toast.warning("请先选择要导出的对话文件");
    return;
  }

  let result;
  try {
    if (!dialogueStylePresets.ready.value) {
      await dialogueStylePresets.retry();
      if (!dialogueStylePresets.ready.value) throw new Error(dialogueStylePresets.error.value || "对话样式预设正在读取，请稍后重试导出。");
    }
    if (!selectStylePresets.ready.value) {
      await selectStylePresets.retry();
      if (!selectStylePresets.ready.value) throw new Error(selectStylePresets.error.value || "选项卡样式预设正在读取，请稍后重试导出。");
    }
    if (!publicEventPresets.ready.value) {
      await publicEventPresets.retry();
      if (!publicEventPresets.ready.value) throw new Error(publicEventPresets.error.value || "公共事件预设正在读取，请稍后重试导出。");
    }
    syncDialoguePublicEventVisibility(dialogueProject.value, publicEventPresets.availablePresets.value);
    syncProjectStyleParameters();
    result = exportQxqyPerformance(workspaceIds.dialogue(dialogueProject.value), dialogueStylePresets.options.value);
  } catch (error) {
    const message = error instanceof Error ? error.message : "结构体 ID 配置无效";
    toast.error(message);
    return;
  }
  if (!result.groupOrder.length) {
    toast.warning("当前对话没有可导出的 Group");
    return;
  }

  const baseName = selectedDialogueFile.value.replace(/\.json$/i, "");
  downloadJsonFile(result.value, `${baseName}-千星演出.json`);
  if (result.warnings.length) {
    consola.warn("千星演出导出提示", result.warnings);
    toast.warning(
      `已导出 ${result.groupOrder.length} 个 Group，另有 ${result.warnings.length} 条提示，请查看控制台`,
    );
  } else {
    toast.success(`已导出 ${result.groupOrder.length} 个 Group`);
  }
}


onBeforeMount(async () => {
  await RefreshDialogueFile();
});

let removeSaveLifecycle: (() => void) | undefined;
onMounted(() => {
  removeSaveLifecycle = bindWorkspaceSaveLifecycle(saveQueue);
});
onBeforeUnmount(() => {
  disposed = true;
  fileRequest++;
  removeSaveLifecycle?.();
  void saveQueue.flush().catch(() => undefined);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "dialogueEditorElement",
    ref: dialogueEditorElement,
    class: "dialogue-editor",
    inert: fileBusy.value || _unref(dialogueHistory).busy.value
  }, [
    _createVNode(StudioSidebarContent, null, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          class: "editor-file-panel",
          inert: fileBusy.value
        }, [
          _createVNode(StudioFileList, {
            disabled: fileBusy.value,
            onSelect: SelectDialogueFile,
            onAdd: _cache[0] || (_cache[0] = ($event: any) => (newDialogueFileOpen.value = true)),
            onDelete: DeleteDialogueFile,
            values: dialogueFiles.value,
            "selected-value": selectedDialogueFile.value
          }, {
            actions: _withCtx(() => [
              _createVNode(RuntimeImportButton, {
                disabled: fileBusy.value,
                "import-file": importConfiguration
              }, null, 8, ["disabled"])
            ]),
            _: 1
          }, 8, ["disabled", "values", "selected-value"])
        ], 8, _hoisted_2)
      ]),
      _: 1
    }),
    _createVNode(SectionLayout, {
      title: selectedDialogueFile.value || '对话编辑',
      class: "dialogue-edit-section"
    }, {
      default: _withCtx(() => [
        (selectedDialogueFile.value && dialogueProject.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("button", {
                      type: "button",
                      "aria-pressed": editorView.value === 'text',
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (ChangeEditorView('text')))
                    }, "文本编辑", 8, _hoisted_7),
                    _createElementVNode("button", {
                      type: "button",
                      "aria-pressed": editorView.value === 'graph',
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (ChangeEditorView('graph')))
                    }, "节点编辑", 8, _hoisted_8)
                  ]),
                  (!_unref(studioSession))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createElementVNode("button", {
                          type: "button",
                          title: "撤销 · Ctrl+Z",
                          disabled: !_unref(dialogueHistory).canUndo.value || arrangingGraph.value,
                          onPointerdown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"])),
                          onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(dialogueHistory).undo && _unref(dialogueHistory).undo(...args)))
                        }, "撤销", 40, _hoisted_10),
                        _createElementVNode("button", {
                          type: "button",
                          title: "重做 · Ctrl+Shift+Z / Ctrl+Y",
                          disabled: !_unref(dialogueHistory).canRedo.value || arrangingGraph.value,
                          onPointerdown: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["prevent"])),
                          onClick: _cache[6] || (_cache[6] = 
//@ts-ignore
(...args) => (_unref(dialogueHistory).redo && _unref(dialogueHistory).redo(...args)))
                        }, "重做", 40, _hoisted_11)
                      ]))
                    : _createCommentVNode("", true),
                  _cache[22] || (_cache[22] = _createElementVNode("span", {
                    class: "studio-save-note",
                    title: "修改自动保存到当前工作区"
                  }, "自动保存", -1)),
                  (editorView.value === 'graph')
                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, "右键平移 · 左键框选 · Ctrl+C / Ctrl+V 复制粘贴"))
                    : _createCommentVNode("", true),
                  (editorView.value === 'graph')
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 2,
                        type: "button",
                        disabled: arrangingGraph.value,
                        title: "按连线从左到右排列全部节点，并适应画布",
                        onClick: ArrangeGraph
                      }, _toDisplayString(arrangingGraph.value ? '排列中…' : '一键排列'), 9, _hoisted_13))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    type: "button",
                    onClick: DownloadQxqyPerformanceFile
                  }, " 导出千星演出 ")
                ]),
                _createElementVNode("div", _hoisted_14, [
                  _createElementVNode("div", _hoisted_15, [
                    _withDirectives(_createElementVNode("div", {
                      class: "dnd-flow",
                      onDrop: _cache[11] || (_cache[11] = 
//@ts-ignore
(...args) => (_unref(onDrop) && _unref(onDrop)(...args)))
                    }, [
                      _createVNode(Sidebar),
                      _createVNode(_unref(VueFlow), {
                        nodes: dialogueProject.value.graph.nodes,
                        "onUpdate:nodes": _cache[7] || (_cache[7] = ($event: any) => ((dialogueProject.value.graph.nodes) = $event)),
                        edges: dialogueProject.value.graph.edges,
                        "onUpdate:edges": _cache[8] || (_cache[8] = ($event: any) => ((dialogueProject.value.graph.edges) = $event)),
                        "pan-on-drag": [2],
                        "selection-key-code": true,
                        "select-nodes-on-drag": true,
                        "pan-activation-key-code": null,
                        "snap-to-grid": true,
                        "snap-grid": [
                   _unref(DIALOGUE_NODE_GRID_SIZE),
                   _unref(DIALOGUE_NODE_GRID_SIZE),
                 ],
                        "delete-key-code": _unref(editorActive)() && editorView.value === 'graph' ? ['Backspace', 'Delete'] : null,
                        "disable-keyboard-a11y": !_unref(editorActive)() || editorView.value !== 'graph',
                        "is-valid-connection": IsValidConnection,
                        onNodeClick: SelectGraphNode,
                        onSelectionEnd: FinishGraphSelection,
                        onPaneClick: _cache[9] || (_cache[9] = () => { if (!clipInspectorOpen.value) selectedGroupNodeId.value = ''; }),
                        onContextmenu: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["prevent"])),
                        onDragover: _unref(onDragOver),
                        onDragleave: _unref(onDragLeave)
                      }, {
                        "node-group": _withCtx((props) => [
                          (
                    props.data.dialogueNodeId &&
                    dialogueProject.value.dialogue.nodes[props.data.dialogueNodeId]
                  )
                            ? (_openBlock(), _createBlock(GroupNode, {
                                key: 0,
                                id: props.id,
                                node: 
                    dialogueProject.value.dialogue.nodes[props.data.dialogueNodeId]
                  ,
                                edges: dialogueProject.value.graph.edges,
                                selected: props.selected,
                                "export-index": exportedGroupIndices.value.get(props.data.dialogueNodeId),
                                annotation: props.data.annotation,
                                "onUpdate:annotation": ($event: any) => (UpdateGraphNodeAnnotation(props.id, $event))
                              }, null, 8, ["id", "node", "edges", "selected", "export-index", "annotation", "onUpdate:annotation"]))
                            : _createCommentVNode("", true)
                        ]),
                        "node-condition": _withCtx((props) => [
                          (
                    props.data.conditionBranchNodeId &&
                    dialogueProject.value.dialogue.conditionBranches[
                      props.data.conditionBranchNodeId
                    ]
                  )
                            ? (_openBlock(), _createBlock(ConditionBranchNode, {
                                key: 0,
                                id: props.id,
                                node: 
                    dialogueProject.value.dialogue.conditionBranches[
                      props.data.conditionBranchNodeId
                    ]
                  ,
                                edges: dialogueProject.value.graph.edges,
                                selected: props.selected,
                                "export-index": exportedGroupIndices.value.get(props.data.conditionBranchNodeId),
                                annotation: props.data.annotation,
                                "onUpdate:annotation": ($event: any) => (UpdateGraphNodeAnnotation(props.id, $event))
                              }, null, 8, ["id", "node", "edges", "selected", "export-index", "annotation", "onUpdate:annotation"]))
                            : _createCommentVNode("", true)
                        ]),
                        "node-entry": _withCtx((props) => [
                          _createVNode(Entry, {
                            id: props.id
                          }, null, 8, ["id"])
                        ]),
                        default: _withCtx(() => [
                          _createVNode(DropzoneBackground, {
                            style: _normalizeStyle({
                  backgroundColor: _unref(isDragOver) ? '#e7f3ff' : 'transparent',
                  transition: 'background-color 0.2s ease',
                })
                          }, {
                            default: _withCtx(() => [
                              (_unref(isDragOver))
                                ? (_openBlock(), _createElementBlock("p", _hoisted_16, "Drop here"))
                                : _createCommentVNode("", true)
                            ]),
                            _: 1
                          }, 8, ["style"])
                        ]),
                        _: 1
                      }, 8, ["nodes", "edges", "snap-grid", "delete-key-code", "disable-keyboard-a11y", "onDragover", "onDragleave"])
                    ], 544), [
                      [_vShow, editorView.value === 'graph']
                    ]),
                    (_unref(dialogueStylePresets).error.value)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_17, [
                          _createTextVNode(_toDisplayString(_unref(dialogueStylePresets).error.value) + " ", 1),
                          _createElementVNode("button", {
                            type: "button",
                            onClick: _cache[12] || (_cache[12] = ($event: any) => (_unref(dialogueStylePresets).retry().catch(() => undefined)))
                          }, "重试对话类型预设")
                        ]))
                      : _createCommentVNode("", true),
                    (_unref(selectStylePresets).error.value)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_18, [
                          _createTextVNode(_toDisplayString(_unref(selectStylePresets).error.value) + " ", 1),
                          _createElementVNode("button", {
                            type: "button",
                            onClick: _cache[13] || (_cache[13] = ($event: any) => (_unref(selectStylePresets).retry().catch(() => undefined)))
                          }, "重试选项卡样式预设")
                        ]))
                      : _createCommentVNode("", true),
                    (editorView.value === 'text')
                      ? (_openBlock(), _createBlock(DialogueTextPreview, {
                          key: selectedDialogueFile.value,
                          project: dialogueProject.value,
                          "managed-history": "",
                          "entity-presets": _unref(entityPresets),
                          "presets-error": _unref(entityPresetsError),
                          onRetryPresets: _cache[14] || (_cache[14] = ($event: any) => (_unref(retryEntityPresets)().catch(() => undefined))),
                          onNavigate: NavigateToPreviewNode,
                          onEdit: EditDialogueText,
                          onOption: EditDialogueOption,
                          onOptionIcon: EditDialogueOptionIcon,
                          onReplace: _cache[15] || (_cache[15] = ($event: any) => (dialogueProject.value = $event))
                        }, null, 8, ["project", "entity-presets", "presets-error"]))
                      : _createCommentVNode("", true),
                    (editorView.value === 'graph' && selectedGroupNode.value)
                      ? (_openBlock(), _createBlock(TimelinePanel, {
                          key: 3,
                          modelValue: timelinePanelHeight.value,
                          "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((timelinePanelHeight).value = $event))
                        }, {
                          default: _withCtx(() => [
                            (_openBlock(), _createBlock(GroupTimeline, {
                              key: selectedDialogueFile.value + ':' + selectedGroupNode.value.id,
                              node: selectedGroupNode.value,
                              "inspector-target": clipInspectorTarget.value,
                              onInspectorOpen: _cache[16] || (_cache[16] = ($event: any) => (clipInspectorOpen.value = $event)),
                              onClose: _cache[17] || (_cache[17] = ($event: any) => (selectedGroupNodeId.value = ''))
                            }, null, 8, ["node", "inspector-target"]))
                          ]),
                          _: 1
                        }, 8, ["modelValue"]))
                      : _createCommentVNode("", true)
                  ]),
                  _withDirectives(_createElementVNode("div", {
                    ref_key: "clipInspectorTarget",
                    ref: clipInspectorTarget,
                    class: "dialogue-clip-inspector",
                    "aria-label": "Clip 属性栏",
                    inert: fileBusy.value
                  }, null, 8, _hoisted_19), [
                    [_vShow, clipInspectorOpen.value && editorView.value === 'graph']
                  ])
                ])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _cache[23] || (_cache[23] = _createElementVNode("h2", null, "开始编写一段对话", -1)),
              _cache[24] || (_cache[24] = _createElementVNode("p", null, "从左侧选择文件，或新建一段对话。", -1)),
              _createElementVNode("button", {
                type: "button",
                onClick: _cache[19] || (_cache[19] = ($event: any) => (newDialogueFileOpen.value = true))
              }, "＋ 新建对话文件")
            ]))
      ]),
      _: 1
    }, 8, ["title"]),
    (newDialogueFileOpen.value)
      ? (_openBlock(), _createBlock(StudioCreateDialog, {
          key: 0,
          modelValue: newDialogueFileName.value,
          "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event: any) => ((newDialogueFileName).value = $event)),
          title: "新建对话文件",
          label: "新对话文件名",
          placeholder: "输入对话文件名",
          "submit-label": "创建并打开",
          busy: fileBusy.value,
          onSubmit: AddDialogueFile,
          onClose: _cache[21] || (_cache[21] = ($event: any) => (newDialogueFileOpen.value = false))
        }, null, 8, ["modelValue", "busy"]))
      : _createCommentVNode("", true)
  ], 8, _hoisted_1))
}
}

})